<template>
  <section
    id="inicio"
    aria-labelledby="hero-title"
    class="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
    role="banner"
  >
    <!-- Background con overlay -->
    <div class="absolute inset-0 z-0">
      <img
        src="/images/hero-bg.jpg"
        alt="Motociclista de MotoBoy realizando entrega express en Santiago de Chile"
        class="w-full h-full object-cover"
        loading="eager"
        onerror="this.style.background='linear-gradient(135deg, #1c0f61 0%, #0b0426 100%)'"
      />
      <!-- Overlay oscuro semi-transparente para mejorar contraste -->
      <div class="absolute inset-0 bg-black/30"></div>
      <!-- Overlay gradiente oscuro a la derecha (como original) -->
      <div
        class="absolute inset-0"
        style="background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 51%, rgba(0,0,0,0.4) 75%, rgba(0,0,0,0) 100%)"
      ></div>
    </div>

    <!-- Content -->
    <div class="container-custom relative z-10 py-20">
      <div class="grid lg:grid-cols-2 gap-12 items-center">
        <!-- Left: CTA Buttons -->
        <header class="space-y-8 animate-fade-in-up">
          <h1 id="hero-title" class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
            {{ $t('hero.title') }}
          </h1>

          <!-- 4 Botones de acceso rápido -->
          <nav aria-label="Acciones principales" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              v-for="(cta, index) in ctaButtons"
              :key="cta.id"
              @click="handleCTAClick(cta.action)"
              :aria-label="$t(`hero.cta.${cta.id}`)"
              type="button"
              :class="[
                'group relative bg-white/10 backdrop-blur-sm hover:bg-primary/90 text-white rounded-xl p-6 text-left transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl border border-white/20',
                `animate-fade-in-up delay-${(index + 1) * 100}`,
              ]"
            >
              <div class="flex items-center gap-4">
                <font-awesome-icon
                  :icon="cta.icon"
                  class="text-3xl text-primary group-hover:text-white transition-colors"
                  aria-hidden="true"
                />
                <h2 class="font-semibold text-base">
                  {{ $t(`hero.cta.${cta.id}`) }}
                </h2>
              </div>

              <!-- Hover border effect -->
              <div class="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-b-xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" aria-hidden="true"></div>
            </button>
          </nav>
        </header>

        <!-- Right: Animated Moto Icon -->
        <aside class="hidden lg:flex justify-center items-center animate-fade-in delay-300" aria-label="Ilustración de motocicleta">
          <div class="relative">
            <!-- Moto icon animado -->
            <div class="w-64 h-64 relative animate-float">
              <img
                src="/images/moto-icon.png"
                alt="Ilustración animada de motocicleta de delivery de MotoBoy Chile"
                class="w-full h-full object-contain drop-shadow-2xl"
                loading="lazy"
                onerror="this.outerHTML='<div class=\\'w-full h-full flex items-center justify-center\\'>
<font-awesome-icon icon=\\'motorcycle\\' class=\\'text-primary text-9xl\\' /></div>'"
              />
            </div>

            <!-- Círculos decorativos -->
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10" aria-hidden="true">
              <div class="w-80 h-80 rounded-full border-2 border-primary/20 animate-ping"></div>
            </div>
          </div>
        </aside>
      </div>

      <!-- Scroll indicator -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a
          href="#servicios"
          @click.prevent="scrollToServices"
          aria-label="Desplazarse a la sección de servicios"
          class="text-white/70 hover:text-white transition-colors"
        >
          <svg
            class="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
interface CTAButton {
  id: string;
  // Excel #14: permitir tuple `[prefix, name]` para íconos de la familia
  // brands (FontAwesome) — necesario para usar el ícono oficial de WhatsApp.
  // Strings simples siguen funcionando para la familia solid (default).
  icon: string | [string, string];
  action: string;
}

const ctaButtons: CTAButton[] = [
  { id: 'login', icon: 'check-circle', action: 'login' },
  { id: 'service', icon: 'motorcycle', action: 'service' },
  { id: 'track', icon: 'box', action: 'track' },
  // Excel #14 (hoja 14.PAGWEB): cambiar "Asistente Virtual" → "Asistente en
  // Línea" + ícono oficial WhatsApp (`[fab, whatsapp]`). Sigue apuntando al
  // canal `wa.me/56992345248`. La library ya carga `faWhatsapp`
  // (`src/main.ts:34`) — no requiere imports adicionales.
  { id: 'assistant', icon: ['fab', 'whatsapp'], action: 'assistant' },
];

const handleCTAClick = (action: string) => {
  // URLs de cliente para registro y login
  const clientBaseUrl = import.meta.env.DEV
    ? 'http://localhost:4200'
    : 'https://client.motoboychile.app';

  const actions: Record<string, string> = {
    login: clientBaseUrl, // Ingreso clientes → Client portal
    service: `${clientBaseUrl}/sign-up`, // Registro nuevo cliente
    track: '/tracking', // Página interna de tracking
    assistant: 'https://wa.me/56992345248',
  };

  const url = actions[action];
  if (url) {
    // Si es ruta interna, usar router, si no abrir en nueva pestaña
    if (url.startsWith('/')) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
};

const scrollToServices = () => {
  const element = document.getElementById('servicios');
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};
</script>

<style scoped>
@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(2deg);
  }
}

.animate-float {
  animation: float 4s ease-in-out infinite;
}
</style>
